// src/components/telemetry/TelemetryPro.jsx
// =============================================================================
// Strumenti di analisi dell'ALLENAMENTO, in stile data logger:
//   DeltaReadout   delta tempo live "alla F1" (numero con segno, non grafico)
//   StripChart     canali sovrapposti su un unico asse distanza, con cursore
//                  condiviso e confronto con il giro di riferimento
//   FrictionCircle diagramma G-G (cerchio di aderenza)
//   ChannelMeters  valori istantanei di accelerometro e giroscopio
// I componenti leggono i ref del genitore (live o replay) e ridisegnano solo
// quando i dati cambiano. Colori: token del tema + colori dati per canale.
// =============================================================================
import React, { useEffect, useMemo, useRef, useState } from 'react';
import './TelemetryPro.css';

/* ---------- formattazione ---------- */
const MINUS = '−';
export const fmtSigned = (v, dec = 3) => {
  if (v == null || !Number.isFinite(v)) return '—';
  const r = Number(v.toFixed(dec));
  if (r === 0) return (0).toFixed(dec);
  return `${r < 0 ? MINUS : '+'}${Math.abs(r).toFixed(dec)}`;
};
export const fmtLapTime = (s, dec = 3) => {
  if (s == null || !Number.isFinite(s) || s < 0) return '—';
  const m = Math.floor(s / 60);
  const sec = s - m * 60;
  return `${m}:${sec.toFixed(dec).padStart(dec ? dec + 3 : 2, '0')}`;
};
// numero con decimali fissi, senza "-0" per i valori che arrotondano a zero
const fixed = (v, dec) => { const t = Number(v).toFixed(dec); return Number(t) === 0 ? (0).toFixed(dec) : t; };
const deltaTone = (v) => (v == null ? 'none' : v < -0.0005 ? 'faster' : v > 0.0005 ? 'slower' : 'even');

/* ---------- colori dal tema ---------- */
function themeColors() {
  const cs = getComputedStyle(document.documentElement);
  const v = (n, f) => cs.getPropertyValue(n).trim() || f;
  return {
    bg: v('--rs-canvas-bg', '#0a0a0c'),
    text0: v('--rs-text-0', '#f5f5f6'),
    text1: v('--rs-text-1', 'rgba(245,245,246,.82)'),
    text2: v('--rs-text-2', 'rgba(245,245,246,.56)'),
    text3: v('--rs-text-3', 'rgba(245,245,246,.36)'),
    line: v('--rs-border-1', 'rgba(255,255,255,.07)'),
    line0: v('--rs-border-0', 'rgba(255,255,255,.12)'),
    strong: v('--rs-border-strong', 'rgba(255,255,255,.22)'),
    surface: v('--rs-surface-0', 'rgba(255,255,255,.03)'),
    success: v('--rs-success', '#22c55e'),
    danger: v('--rs-danger', '#ef4444'),
    mono: v('--font-mono', 'monospace'),
  };
}
// colore canale: accetta un token css (--rs-…) o un colore dato
const resolveColor = (c) => {
  if (!c || !c.startsWith('--')) return c || '#fff';
  return getComputedStyle(document.documentElement).getPropertyValue(c).trim() || '#fff';
};

/* ---------- serie: ricerca, filtro, decimazione ---------- */
function indexAtDist(pts, d) {
  let lo = 0, hi = pts.length - 1;
  while (lo < hi) { const mid = (lo + hi) >> 1; if (pts[mid].t < d) lo = mid + 1; else hi = mid; }
  return lo;
}
function valueAtDist(pts, d) {
  if (!pts || pts.length === 0) return null;
  if (d <= pts[0].t) return pts[0].value;
  const last = pts[pts.length - 1];
  if (d >= last.t) return d - last.t > 25 ? null : last.value;
  const i = indexAtDist(pts, d);
  const a = pts[i - 1], b = pts[i];
  if (!a) return b.value;
  return b.t === a.t ? b.value : a.value + (b.value - a.value) * (d - a.t) / (b.t - a.t);
}
function elapsedAtDist(pts, d) {
  if (!pts || pts.length < 2) return null;
  if (d <= pts[0].t) return pts[0].elapsed ?? null;
  const last = pts[pts.length - 1];
  if (d > last.t) return null;
  const i = indexAtDist(pts, d);
  const a = pts[i - 1], b = pts[i];
  if (!a || a.elapsed == null || b.elapsed == null) return null;
  return b.t === a.t ? a.elapsed : a.elapsed + (b.elapsed - a.elapsed) * (d - a.t) / (b.t - a.t);
}
// Media mobile centrata (filtro di visualizzazione), con cache per serie.
const smoothCache = new WeakMap();
function smoothed(pts, win) {
  if (!pts || pts.length < 3 || win <= 1) return pts || [];
  const c = smoothCache.get(pts);
  if (c && c.len === pts.length && c.win === win) return c.out;
  const half = Math.floor(win / 2);
  const out = new Array(pts.length);
  let sum = 0, n = 0;
  // finestra scorrevole
  for (let i = 0; i < Math.min(half, pts.length); i++) { sum += pts[i].value; n++; }
  for (let i = 0; i < pts.length; i++) {
    const add = i + half, rem = i - half - 1;
    if (add < pts.length) { sum += pts[add].value; n++; }
    if (rem >= 0) { sum -= pts[rem].value; n--; }
    out[i] = { t: pts[i].t, value: sum / n, elapsed: pts[i].elapsed };
  }
  smoothCache.set(pts, { len: pts.length, win, out });
  return out;
}
// Per serie fitte: min e max per colonna di pixel (l'inviluppo resta fedele).
function decimate(pts, x0, xSpan, plotW) {
  if (pts.length <= plotW * 2) return pts;
  const out = [];
  let col = -1, mn = null, mx = null;
  const flush = () => {
    if (!mn) return;
    if (mn === mx) out.push(mn);
    else if (mn.t < mx.t) out.push(mn, mx); else out.push(mx, mn);
  };
  for (const p of pts) {
    const c = Math.floor(((p.t - x0) / xSpan) * plotW);
    if (c !== col) { flush(); col = c; mn = p; mx = p; }
    else { if (p.value < mn.value) mn = p; if (p.value > mx.value) mx = p; }
  }
  flush();
  return out;
}

/* ═══════════════════════════════════════════════════════════════════════════
   DELTA LIVE (numero con segno, stile F1)
   ═══════════════════════════════════════════════════════════════════════════ */
export function DeltaReadout({ value, refInfo, last, lastFresh, size = 'lg', note }) {
  const tone = deltaTone(value);
  const showLast = lastFresh && last && Number.isFinite(last.value);
  const shown = showLast ? last.value : value;
  const shownTone = showLast ? deltaTone(last.value) : tone;
  // barra: ±1 s a fondo scala
  const frac = shown == null ? 0 : Math.max(-1, Math.min(1, shown / 1));
  const refLabel = refInfo
    ? [
      refInfo.kind === 'instructor' ? 'vs istruttore' : refInfo.kind === 'best' ? 'vs best' : 'vs giro',
      refInfo.lap != null ? `L${refInfo.lap}` : null,
      refInfo.lapTime ? fmtLapTime(refInfo.lapTime) : null,
    ].filter(Boolean).join(' · ')
    : null;
  return (
    <div className={`tx-delta tx-delta--${size} is-${shownTone}${showLast ? ' is-final' : ''}`}>
      <div className="tx-delta__head">
        <span className="tx-delta__label">{showLast ? `Giro ${last.lapNum} · finale` : 'Delta'}</span>
        {refLabel && <span className="tx-delta__ref">{refLabel}</span>}
      </div>
      <div className="tx-delta__value" aria-live="off">{fmtSigned(shown)}</div>
      <div className="tx-delta__bar" aria-hidden="true">
        <span className="tx-delta__zero" />
        {shown != null && (
          <span
            className="tx-delta__fill"
            style={frac < 0
              ? { right: '50%', width: `${Math.abs(frac) * 50}%` }
              : { left: '50%', width: `${frac * 50}%` }}
          />
        )}
      </div>
      {note && shown == null && <div className="tx-delta__note">{note}</div>}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   STRIP CHART — canali sovrapposti, asse distanza condiviso
   ═══════════════════════════════════════════════════════════════════════════ */
export function StripChart({
  lapDataRef, compareLapRef, hoverRef, channels, stripHeight = 78,
  filterWin = 1, live = false, accent,
}) {
  const wrapRef = useRef(null);
  const canvasRef = useRef(null);
  const layoutRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const wrap = wrapRef.current;
    if (!canvas || !wrap) return undefined;
    const ctx = canvas.getContext('2d');
    let raf = 0, lastSig = '';
    const HEAD = 26, AXIS = 22, PADR = 10;
    const totalH = HEAD + channels.length * stripHeight + AXIS;

    const draw = () => {
      raf = requestAnimationFrame(draw);
      const ld = lapDataRef.current;
      if (!ld) return;
      const ref = compareLapRef?.current ?? ld.prev ?? null;
      const cur = ld.current || {};
      const w = wrap.clientWidth || 600;
      const dpr = window.devicePixelRatio || 1;
      const hover = hoverRef?.current?.dist ?? null;
      const theme = document.documentElement.getAttribute('data-theme') || '';
      const lenSig = channels.map(c => `${cur[c.key]?.length || 0}/${ref?.[c.key]?.length || 0}`).join(',');
      const sig = `${w}|${dpr}|${hover}|${theme}|${lenSig}|${ld.currentLapNum}|${ref?.lapNum}|${filterWin}|${Math.round(ld.currentDist || 0)}`;
      if (sig === lastSig) return;
      lastSig = sig;

      if (canvas.width !== Math.round(w * dpr) || canvas.height !== Math.round(totalH * dpr)) {
        canvas.width = Math.round(w * dpr); canvas.height = Math.round(totalH * dpr);
        canvas.style.width = `${w}px`; canvas.style.height = `${totalH}px`;
      }
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      const T = themeColors();
      const G = w < 560 ? 92 : 132;
      const plotX = G, plotW = Math.max(40, w - G - PADR);
      const curDist = live ? (ld.currentDist || 0) : (cur.totalDist || ld.currentDist || 0);
      const maxDist = Math.max(100, ref?.totalDist || 0, curDist);
      layoutRef.current = { plotX, plotW, maxDist, top: HEAD, bottom: HEAD + channels.length * stripHeight };
      const xOf = (d) => plotX + (d / maxDist) * plotW;

      ctx.clearRect(0, 0, w, totalH);
      ctx.fillStyle = T.bg; ctx.fillRect(0, 0, w, totalH);

      // ── intestazione: legenda giri + lettura del cursore ──
      ctx.font = `600 10px ${T.mono}`;
      ctx.textBaseline = 'middle';
      ctx.textAlign = 'left';
      const accentCol = resolveColor(accent) || T.text0;
      let hx = plotX;
      ctx.fillStyle = accentCol; ctx.fillRect(hx, HEAD / 2 - 1, 14, 2); hx += 20;
      // in live currentLapNum conta i giri completati: quello in corso è il successivo
      ctx.fillStyle = T.text1; const curLbl = live ? `LAP ${(ld.currentLapNum || 0) + 1} · IN CORSO` : `LAP ${ld.currentLapNum || '—'}`;
      ctx.fillText(curLbl, hx, HEAD / 2); hx += ctx.measureText(curLbl).width + 18;
      if (ref) {
        ctx.strokeStyle = T.text2; ctx.lineWidth = 1; ctx.setLineDash([3, 3]);
        ctx.beginPath(); ctx.moveTo(hx, HEAD / 2); ctx.lineTo(hx + 14, HEAD / 2); ctx.stroke(); ctx.setLineDash([]);
        hx += 20; ctx.fillStyle = T.text2; ctx.fillText(`LAP ${ref.lapNum ?? '—'} · CONFRONTO`, hx, HEAD / 2);
      }
      if (hover != null && hover <= maxDist) {
        const eC = elapsedAtDist(cur.speed, hover), eR = ref ? elapsedAtDist(ref.speed, hover) : null;
        const dt = eC != null && eR != null ? eC - eR : null;
        ctx.textAlign = 'right';
        const distTxt = hover >= 1000 ? `${(hover / 1000).toFixed(3)} km` : `${Math.round(hover)} m`;
        let rx = w - PADR;
        if (dt != null) {
          ctx.font = `700 11px ${T.mono}`;
          ctx.fillStyle = dt < 0 ? T.success : dt > 0 ? T.danger : T.text1;
          const s = `Δt ${fmtSigned(dt)}`;
          ctx.fillText(s, rx, HEAD / 2); rx -= ctx.measureText(s).width + 14;
        }
        ctx.font = `600 10px ${T.mono}`; ctx.fillStyle = T.text1;
        ctx.fillText(distTxt, rx, HEAD / 2);
      }

      // ── strisce ──
      channels.forEach((ch, ci) => {
        const y0 = HEAD + ci * stripHeight;
        const pT = y0 + 8, pB = y0 + stripHeight - 8, pH = pB - pT;
        const color = resolveColor(ch.color);
        const curPts = smoothed(cur[ch.key] || [], filterWin);
        const refPts = ref ? smoothed(ref[ch.key] || [], filterWin) : [];

        // scala
        let mn = Infinity, mx = -Infinity;
        for (const p of curPts) { if (p.value < mn) mn = p.value; if (p.value > mx) mx = p.value; }
        for (const p of refPts) { if (p.value < mn) mn = p.value; if (p.value > mx) mx = p.value; }
        if (!Number.isFinite(mn)) { mn = ch.signed ? -1 : 0; mx = 1; }
        if (ch.signed) { const a = Math.max(Math.abs(mn), Math.abs(mx), (ch.minSpan || 1) / 2); mn = -a; mx = a; }
        else {
          if (ch.floor != null) mn = Math.min(mn, ch.floor);
          const span = mx - mn, need = ch.minSpan || 1;
          if (span < need) { const c = (mx + mn) / 2; mn = c - need / 2; mx = c + need / 2; }
          const pad = (mx - mn) * 0.08; mx += pad; if (ch.floor == null) mn -= pad;
        }
        const yOf = (v) => pB - ((v - mn) / ((mx - mn) || 1)) * pH;

        // fondo striscia e separatore
        if (ci % 2 === 1) { ctx.fillStyle = T.surface; ctx.fillRect(0, y0, w, stripHeight); }
        ctx.fillStyle = T.line; ctx.fillRect(0, y0, w, 1);
        // griglia verticale (ogni 1/8 di giro)
        ctx.fillStyle = T.line;
        for (let k = 1; k < 8; k++) ctx.fillRect(Math.round(plotX + (plotW * k) / 8), y0 + 1, 1, stripHeight - 1);
        // zero
        if (ch.signed || (mn < 0 && mx > 0)) {
          ctx.strokeStyle = T.line0; ctx.lineWidth = 1; ctx.setLineDash([2, 3]);
          ctx.beginPath(); ctx.moveTo(plotX, Math.round(yOf(0)) + 0.5); ctx.lineTo(plotX + plotW, Math.round(yOf(0)) + 0.5); ctx.stroke(); ctx.setLineDash([]);
        }
        // fondo scala
        ctx.font = `500 9px ${T.mono}`; ctx.fillStyle = T.text3; ctx.textAlign = 'left';
        ctx.textBaseline = 'top'; ctx.fillText(fixed(mx, ch.scaleDec ?? 1), plotX + 4, pT - 4);
        ctx.textBaseline = 'bottom'; ctx.fillText(fixed(mn, ch.scaleDec ?? 1), plotX + 4, pB + 4);

        // tracce
        const line = (pts, stroke, width, dash) => {
          if (pts.length < 2) return;
          const dp = decimate(pts, 0, maxDist, plotW);
          ctx.save();
          ctx.beginPath(); ctx.rect(plotX, y0 + 1, plotW, stripHeight - 1); ctx.clip();
          ctx.strokeStyle = stroke; ctx.lineWidth = width; ctx.lineJoin = 'round';
          if (dash) ctx.setLineDash(dash);
          ctx.beginPath();
          dp.forEach((p, i) => { const x = xOf(p.t), y = yOf(p.value); if (i) ctx.lineTo(x, y); else ctx.moveTo(x, y); });
          ctx.stroke();
          ctx.restore();
        };
        line(refPts, T.text2, 1, [3, 2]);
        line(curPts, color, 1.4);

        // gutter: nome canale, unità e valore (cursore o ultimo)
        ctx.fillStyle = color; ctx.fillRect(10, y0 + 12, 2, 12);
        ctx.textBaseline = 'middle'; ctx.textAlign = 'left';
        ctx.font = `600 9.5px ${T.mono}`; ctx.fillStyle = T.text1;
        ctx.fillText(ch.label.toUpperCase(), 18, y0 + 18);
        ctx.font = `500 9px ${T.mono}`; ctx.fillStyle = T.text3;
        ctx.fillText(ch.unit, 18 + ctx.measureText(ch.label.toUpperCase()).width * (9.5 / 9) + 6, y0 + 18);
        const at = hover != null ? hover : null;
        const vCur = at != null ? valueAtDist(curPts, at) : (curPts.length ? curPts[curPts.length - 1].value : null);
        const vRef = at != null && refPts.length ? valueAtDist(refPts, at) : null;
        ctx.font = `700 ${G < 120 ? 14 : 17}px ${T.mono}`; ctx.fillStyle = T.text0;
        ctx.fillText(vCur == null ? '—' : fixed(vCur, ch.decimals ?? 1), 16, y0 + 40);
        if (vRef != null) {
          ctx.font = `500 9.5px ${T.mono}`; ctx.fillStyle = T.text2;
          ctx.fillText(`rif ${fixed(vRef, ch.decimals ?? 1)}`, 16, y0 + 58);
        }
        // cursore: punti sulle tracce
        if (at != null && at <= maxDist) {
          const x = xOf(at);
          if (vRef != null) { ctx.fillStyle = T.text2; ctx.beginPath(); ctx.arc(x, yOf(vRef), 2.5, 0, Math.PI * 2); ctx.fill(); }
          if (vCur != null) { ctx.fillStyle = color; ctx.beginPath(); ctx.arc(x, yOf(vCur), 3, 0, Math.PI * 2); ctx.fill(); }
        }
      });

      // linea del gutter e cursore verticale
      const bottom = HEAD + channels.length * stripHeight;
      ctx.fillStyle = T.line0; ctx.fillRect(plotX - 1, HEAD, 1, bottom - HEAD);
      ctx.fillStyle = T.line; ctx.fillRect(0, bottom, w, 1);
      if (live && curDist > 0) {
        const x = Math.round(xOf(curDist)) + 0.5;
        ctx.strokeStyle = accentCol; ctx.globalAlpha = 0.55; ctx.lineWidth = 1;
        ctx.beginPath(); ctx.moveTo(x, HEAD); ctx.lineTo(x, bottom); ctx.stroke(); ctx.globalAlpha = 1;
      }
      if (hover != null && hover <= maxDist) {
        const x = Math.round(xOf(hover)) + 0.5;
        ctx.strokeStyle = T.text1; ctx.lineWidth = 1;
        ctx.beginPath(); ctx.moveTo(x, HEAD); ctx.lineTo(x, bottom); ctx.stroke();
      }

      // asse distanza
      ctx.font = `500 9.5px ${T.mono}`; ctx.fillStyle = T.text2; ctx.textBaseline = 'middle';
      const steps = plotW < 360 ? 4 : 8;
      for (let k = 0; k <= steps; k++) {
        const d = (maxDist * k) / steps;
        ctx.textAlign = k === 0 ? 'left' : k === steps ? 'right' : 'center';
        ctx.fillText(d >= 1000 ? `${(d / 1000).toFixed(2)} km` : `${Math.round(d)} m`, plotX + (plotW * k) / steps, bottom + AXIS / 2);
      }
    };
    raf = requestAnimationFrame(draw);

    const pick = (clientX) => {
      const L = layoutRef.current; if (!L || !hoverRef?.current) return;
      const rect = canvas.getBoundingClientRect();
      const f = (clientX - rect.left - L.plotX) / L.plotW;
      hoverRef.current.dist = f < 0 || f > 1 ? null : f * L.maxDist;
    };
    const onMove = (e) => pick(e.clientX);
    const onLeave = () => { if (hoverRef?.current) hoverRef.current.dist = null; };
    const onTouch = (e) => { if (e.touches?.[0]) { e.preventDefault(); pick(e.touches[0].clientX); } };
    canvas.addEventListener('pointermove', onMove);
    canvas.addEventListener('pointerleave', onLeave);
    canvas.addEventListener('touchmove', onTouch, { passive: false });
    canvas.addEventListener('touchend', onLeave);
    return () => {
      cancelAnimationFrame(raf);
      canvas.removeEventListener('pointermove', onMove);
      canvas.removeEventListener('pointerleave', onLeave);
      canvas.removeEventListener('touchmove', onTouch);
      canvas.removeEventListener('touchend', onLeave);
    };
  }, [lapDataRef, compareLapRef, hoverRef, channels, stripHeight, filterWin, live, accent]);

  return (
    <div ref={wrapRef} className="tx-strip">
      <canvas ref={canvasRef} className="tx-strip__canvas" />
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   CERCHIO DI ADERENZA (G-G)
   ═══════════════════════════════════════════════════════════════════════════ */
export function FrictionCircle({ bufferRef, accent, staticMode = false, size = 260 }) {
  const canvasRef = useRef(null);
  const wrapRef = useRef(null);
  const [read, setRead] = useState({ lat: null, long: null, comb: null, peakComb: null });

  useEffect(() => {
    const canvas = canvasRef.current, wrap = wrapRef.current;
    if (!canvas || !wrap) return undefined;
    const ctx = canvas.getContext('2d');
    let raf = 0, lastDraw = 0, lastRead = 0, scaleG = 1.5;
    const TRAIL_MS = 10000;

    const draw = (now) => {
      raf = requestAnimationFrame(draw);
      if (now - lastDraw < 33) return; // ~30 fps bastano
      lastDraw = now;
      const s = Math.min(size, wrap.clientWidth || size);
      const dpr = window.devicePixelRatio || 1;
      if (canvas.width !== Math.round(s * dpr)) {
        canvas.width = Math.round(s * dpr); canvas.height = Math.round(s * dpr);
        canvas.style.width = `${s}px`; canvas.style.height = `${s}px`;
      }
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      const T = themeColors();
      const acc = resolveColor(accent) || T.text0;
      const cx = s / 2, cy = s / 2, r = s / 2 - 22;
      const lat = bufferRef.current?.gLat || [], lng = bufferRef.current?.gLong || [];
      const n = Math.min(lat.length, lng.length);
      const t = Date.now();

      // scala: gradini di 0.5 G, adattata al picco recente
      let peak = 0, peakComb = 0;
      for (let i = 0; i < n; i++) {
        if (!staticMode && lat[i].ts < t - TRAIL_MS) continue;
        const a = Math.abs(lat[i].value), b = Math.abs(lng[i].value);
        peak = Math.max(peak, a, b);
        peakComb = Math.max(peakComb, Math.hypot(lat[i].value, lng[i].value));
      }
      const target = Math.max(1, peak > 2 ? Math.ceil(peak * 1.1) : Math.ceil((peak * 1.1) / 0.5) * 0.5);
      scaleG += (target - scaleG) * (target > scaleG ? 0.25 : 0.03);
      const S = scaleG;

      ctx.clearRect(0, 0, s, s);
      // anelli ogni 0.5 G (ogni 1 G oltre i 2 G, per restare leggibili)
      ctx.lineWidth = 1;
      const step = S > 2.01 ? 1 : 0.5;
      for (let g = step; g <= S + 1e-6; g += step) {
        const rr = (g / S) * r;
        ctx.strokeStyle = g >= S - 1e-6 ? T.strong : Math.abs(g - Math.round(g)) < 1e-6 ? T.line0 : T.line;
        ctx.beginPath(); ctx.arc(cx, cy, rr, 0, Math.PI * 2); ctx.stroke();
        ctx.font = `500 9px ${T.mono}`; ctx.fillStyle = T.text3; ctx.textAlign = 'left'; ctx.textBaseline = 'bottom';
        ctx.fillText(`${g.toFixed(1)}`, cx + rr * 0.707 + 2, cy - rr * 0.707 - 1);
      }
      ctx.strokeStyle = T.line0;
      ctx.beginPath(); ctx.moveTo(cx - r - 6, cy); ctx.lineTo(cx + r + 6, cy); ctx.moveTo(cx, cy - r - 6); ctx.lineTo(cx, cy + r + 6); ctx.stroke();
      // etichette assi
      ctx.font = `600 9px ${T.mono}`; ctx.fillStyle = T.text2; ctx.textBaseline = 'middle';
      ctx.textAlign = 'center';
      ctx.fillText('FRENATA', cx, 9); ctx.fillText('TRAZIONE', cx, s - 9);
      ctx.textAlign = 'left'; ctx.fillText('SX', 3, cy - 8);
      ctx.textAlign = 'right'; ctx.fillText('DX', s - 3, cy - 8);

      // scia
      const px = (v) => cx + (Math.max(-S, Math.min(S, v)) / S) * r;
      const py = (v) => cy - (Math.max(-S, Math.min(S, v)) / S) * r;
      for (let i = 0; i < n; i++) {
        const age = staticMode ? 0 : t - lat[i].ts;
        if (!staticMode && age > TRAIL_MS) continue;
        const a = staticMode ? 0.35 : 0.08 + 0.5 * (1 - age / TRAIL_MS);
        ctx.globalAlpha = a; ctx.fillStyle = T.text1;
        ctx.fillRect(px(lat[i].value) - 1, py(lng[i].value) - 1, 2, 2);
      }
      ctx.globalAlpha = 1;
      // punto attuale
      if (n > 0 && !staticMode) {
        const x = px(lat[n - 1].value), y = py(lng[n - 1].value);
        ctx.strokeStyle = acc; ctx.lineWidth = 1;
        ctx.beginPath(); ctx.moveTo(cx, cy); ctx.lineTo(x, y); ctx.stroke();
        ctx.fillStyle = acc; ctx.beginPath(); ctx.arc(x, y, 4.5, 0, Math.PI * 2); ctx.fill();
        ctx.strokeStyle = T.bg; ctx.lineWidth = 1.5; ctx.stroke();
      }
      if (now - lastRead > 150) {
        lastRead = now;
        const L = n ? lat[n - 1].value : null, G2 = n ? lng[n - 1].value : null;
        setRead({ lat: staticMode ? null : L, long: staticMode ? null : G2, comb: staticMode || L == null ? null : Math.hypot(L, G2), peakComb: n ? peakComb : null });
      }
    };
    raf = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(raf);
  }, [bufferRef, accent, staticMode, size]);

  const f = (v) => (v == null ? '—' : v.toFixed(2));
  return (
    <div className="tx-gg">
      <div ref={wrapRef} className="tx-gg__plot" style={{ maxWidth: size }}><canvas ref={canvasRef} /></div>
      <dl className="tx-gg__read">
        <div><dt>Lat</dt><dd>{f(read.lat)}</dd></div>
        <div><dt>Long</dt><dd>{f(read.long)}</dd></div>
        <div><dt>Comb</dt><dd>{f(read.comb)}</dd></div>
        <div><dt>Picco</dt><dd>{f(read.peakComb)}</dd></div>
      </dl>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   STRUMENTI IMU — valore istantaneo (o al cursore) con barra a zero centrale
   e minimo/massimo del giro
   ═══════════════════════════════════════════════════════════════════════════ */
export function ChannelMeters({ lapDataRef, hoverRef, groups, live = false }) {
  const [vals, setVals] = useState({});
  const allChannels = useMemo(() => groups.flatMap(g => g.channels), [groups]);

  useEffect(() => {
    let t = 0;
    const tick = () => {
      const ld = lapDataRef.current;
      const cur = ld?.current || {};
      const hover = hoverRef?.current?.dist ?? null;
      const out = {};
      for (const ch of allChannels) {
        const pts = cur[ch.key] || [];
        let mn = null, mx = null;
        for (const p of pts) {
          if (mn == null || p.value < mn) mn = p.value;
          if (mx == null || p.value > mx) mx = p.value;
        }
        const v = hover != null ? valueAtDist(pts, hover) : (live && pts.length ? pts[pts.length - 1].value : null);
        out[ch.key] = { v, mn, mx };
      }
      setVals(out);
      t = setTimeout(tick, 125);
    };
    tick();
    return () => clearTimeout(t);
  }, [lapDataRef, hoverRef, allChannels, live]);

  return (
    <div className="tx-meters">
      {groups.map(g => (
        <div key={g.title} className="tx-meters__group">
          <div className="tx-meters__title"><span>{g.title}</span><em>{g.unit}</em></div>
          {g.channels.map(ch => {
            const r = vals[ch.key] || {};
            const full = Math.max(ch.range, Math.abs(r.mn ?? 0), Math.abs(r.mx ?? 0));
            const pos = (v) => `${50 + (Math.max(-full, Math.min(full, v)) / full) * 50}%`;
            const v = r.v;
            return (
              <div key={ch.key} className="tx-meter" style={{ '--ch': ch.color }}>
                <span className="tx-meter__name">{ch.label}<small>{ch.axis}</small></span>
                <span className="tx-meter__track">
                  <span className="tx-meter__mid" />
                  {r.mn != null && <span className="tx-meter__range" style={{ left: pos(r.mn), width: `calc(${pos(r.mx)} - ${pos(r.mn)})` }} />}
                  {v != null && <span className="tx-meter__fill" style={v < 0 ? { left: pos(v), width: `calc(50% - ${pos(v)})` } : { left: '50%', width: `calc(${pos(v)} - 50%)` }} />}
                  {v != null && <span className="tx-meter__tick" style={{ left: pos(v) }} />}
                </span>
                <span className="tx-meter__val">{v == null ? '—' : fmtSigned(v, ch.decimals ?? 2)}</span>
                <span className="tx-meter__mm">{r.mn == null ? '—' : `${fmtSigned(r.mn, ch.decimals ?? 2)} / ${fmtSigned(r.mx, ch.decimals ?? 2)}`}</span>
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}
